import React from 'react'
import ResearchDashboardDataRing from '../common/ResearchDashboardDataRing'
import ResearchDashboardChart from './ResearchDashboardChart'
import { MetricDefinition } from './ResearchDashboardMetrics'
import { useResearchDashboardMetricsQuery } from '../../../backend/Queries'
import type { MetricKey } from '../../../common/types/ResearchDashboardMetricsDTO'
import ResearchDashboardGroupOverview from './ResearchDashboardGroupOverview'
import I18n from 'i18n'

interface Props {
  definitions: MetricDefinition[]
  onCardClick?: (metricId: string) => void
  selectedMetric: string | null
}

const DEFAULT_METRIC: MetricKey = 'watchWeartime'

// Map metric IDs to their default periods for pie charts
const METRIC_PIE_PERIODS: Record<MetricKey, 'daily' | 'weekly' | 'monthly'> = {
  watchWeartime: 'daily',
  ecg: 'weekly',
  questionnaires: 'monthly'
}

const ResearchDashboardMetricsGrid: React.FC<Props> = ({ definitions, onCardClick, selectedMetric }) => {
  // Fetch data for pie charts (always offset 0 since it is always current data)
  const { data: dailyData } = useResearchDashboardMetricsQuery('daily', 0)
  const { data: weeklyData } = useResearchDashboardMetricsQuery('weekly', 0)
  const { data: monthlyData } = useResearchDashboardMetricsQuery('monthly', 0)

  // Use selectedMetric or default to watchWeartime
  const activeMetric = (selectedMetric ?? DEFAULT_METRIC) as MetricKey

  const dataByPeriod = {
    daily: dailyData,
    weekly: weeklyData,
    monthly: monthlyData
  }

  return (
    <div className='research-dashboard-layout'>
      <div className='research-dashboard-compliance-box'>
        <h3 className='research-dashboard-compliance-title'>{I18n.t('components.dashboards.research_dashboard.compliance')}</h3>
        <div className='research-dashboard-grid research-dashboard-rings'>
          {definitions
            .map(def => {
              const metricLabel = def.labelKey
              const timePeriodType = METRIC_PIE_PERIODS[def.id as MetricKey]
              const metricDataInTimePeriod = dataByPeriod[timePeriodType]
              const currentMetricDataByID = metricDataInTimePeriod?.[def.id as keyof typeof metricDataInTimePeriod]
              const compliancePercentage = currentMetricDataByID?.current?.percent ?? null

              const isSelected = selectedMetric === def.id || (selectedMetric === null && def.id === DEFAULT_METRIC)

              return (
                <div
                  key={def.id}
                  className={`research-metric-card research-metric-card-clickable ${isSelected ? 'selected' : ''}`}
                  onClick={() => onCardClick?.(def.id)}
                  role='button'
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && onCardClick?.(def.id)}
                >
                  <h5 className='research-metric-card-title'>
                    {def.cardTitle}
                  </h5>

                  <div className='research-metric-card-content'>
                    <ResearchDashboardDataRing
                      label={metricLabel}
                      percent={compliancePercentage}
                    />
                  </div>
                </div>
              )
            })}
        </div>

        <ResearchDashboardChart selectedMetric={activeMetric} />
      </div>
      <div className='research-dashboard-group-overview-cell'>
        <ResearchDashboardGroupOverview />
      </div>
    </div>
  )
}

export default ResearchDashboardMetricsGrid
